import { Response } from 'miragejs';
import { sleep } from './utils';
import {
    listSimulatorClusters
} from './aiSimulatorAssets';
import deleteVolumeWorkflow from './data/chat/delete_volume.json';
import stopScenarios from './data/chat/stop_scenarios.json';

// ============================================================================
// Import all chat JSON files from data/chat/
// Each file represents a recorded conversation with a unique user prompt.
// To add a new conversation, simply drop a new HAR-recorded JSON file here.
// ============================================================================
import chatAnalyzeCpu from "./data/chat/analyze_cpu.json";
import chatCreateVol from "./data/chat/create_vol.json";
import chatCreateVolDynamicForm from "./data/chat/create_vol_dynamic_form.json";
import chatCreateVolLayoutForm from "./data/chat/create_vol_layout_form.json";
import chatCreateVolClasses from "./data/chat/create_vol_classes.json";
import chatWelcome from "./data/chat/welcome.json";
import chatBlockedContent from "./data/chat/blocked_content.json";
import chatErrorApi from "./data/chat/error_api.json";
import chatWorkloadPerformance from "./data/chat/workload_performance.json";
import chatAlertOverview from "./data/chat/alert_overview.json";
import chatRecommendCluster from "./data/chat/recommend_cluster.json";
import chatVolOver85 from "./data/chat/vol_over_85.json";
import chatListVolumes from "./data/chat/list_volumes.json";
import chatNoisyCluster from "./data/chat/noisiest_cluster.json";
import chatStorageClasses from "./data/chat/storage_classes.json";
import chatCreateIgroupForm from "./data/chat/create_igroup_form.json";
import chatCreateIgroup from "./data/chat/create_igroup.json";
import chatGetRecommendation from "./data/chat/get_recommendation.json";

/**
 * Registry of all imported chat conversation JSON files.
 * Add new imports above and include them in this array.
 * @type {Array<Array<Object>>}
 */
const allChatRecordings = [
    chatAnalyzeCpu,
    chatCreateVol,
    chatCreateVolDynamicForm,
    chatCreateVolLayoutForm,
    chatCreateVolClasses,
    chatWelcome,
    chatBlockedContent,
    chatErrorApi,
    chatWorkloadPerformance,
    chatAlertOverview,
    chatRecommendCluster,
    chatVolOver85,
    chatListVolumes,
    chatNoisyCluster,
    chatStorageClasses,
    chatCreateIgroupForm,
    chatCreateIgroup,
    chatGetRecommendation
];

// ============================================================================
// Multi-Turn Conversation Parsing
// ============================================================================

/**
 * Checks whether a "send" entry is an actual user message (contains a messages array)
 * as opposed to a ping or other control message.
 * @param {Object} entry - A HAR recording entry.
 * @returns {boolean}
 */
const isUserMessage = (entry) => {
    if (entry.type !== 'send') return false;
    try {
        const parsed = JSON.parse(entry.data);
        return !!(parsed.messages && Array.isArray(parsed.messages));
    } catch {
        return false;
    }
};

/**
 * Extracts the last user message content from a send entry's data.
 * In multi-turn conversations, each send contains the full conversation history,
 * so the last user message is the newest prompt for that turn.
 * @param {Object} sendEntry - A "send" HAR entry.
 * @returns {string|null} - The user's message content, or null.
 */
const extractLastUserContent = (sendEntry) => {
    try {
        const parsed = JSON.parse(sendEntry.data);
        const userMessages = parsed.messages?.filter(m => m.role === 'user') || [];
        const lastUserMsg = userMessages[userMessages.length - 1];
        return lastUserMsg?.content?.toLowerCase()?.trim() || null;
    } catch {
        return null;
    }
};

/** Content type the chat sends back when a SkillPermission card is answered. */
const SKILL_PERMISSION_RESPONSE_TYPE = 'skill_permission_response';
const FORM_RESPONSE_TYPE = 'form_response';

// Matched loosely rather than by equality: an unrecognized prompt falls through
// to word-overlap matching against the recordings, which would silently replay
// an unrelated conversation instead of the capability-driven scenario.
const SKILL_PERMISSION_PROMPT_PATTERN = /skill permission/;
const MODE_CONTEXT = 'mode';
const SKILL_PERMISSION_SESSION = 'skill-permission';
const STOP_SCENARIO_SESSION = 'stop-scenario';
const VALID_CAPABILITY_LEVELS = new Set(['off', 'ask', 'allow']);
const DEFAULT_CAPABILITY_CONTEXT_BY_KEY = {
    analyzer: 'capability:analyzer',
    storage_management: 'capability:storage_management'
};
const SIMULATOR_WORKFLOWS = [deleteVolumeWorkflow];
const STOP_SCENARIOS = [...stopScenarios.workflows, ...stopScenarios.scenarios];

/**
 * Converts the request context array into a name/value lookup.
 * Unknown and malformed entries are ignored so recordings remain permissive.
 */
const parseRequestContext = (request) => (request?.context || []).reduce((result, entry) => {
    if (entry && typeof entry.name === 'string') {
        result[entry.name] = entry.value;
    }
    return result;
}, {});

/** An unset or unrecognized level is treated as "ask", the safest gate. */
const getCapabilityLevel = (context, capabilityKey, workflow) => {
    const contextName = workflow?.capabilities?.[capabilityKey]?.contextName
        || DEFAULT_CAPABILITY_CONTEXT_BY_KEY[capabilityKey];
    const level = context[contextName];
    return VALID_CAPABILITY_LEVELS.has(level) ? level : 'ask';
};

const matchesSimulatorWorkflow = (workflow, prompt) => {
    try {
        return new RegExp(workflow.promptPattern, 'i').test(prompt);
    } catch (_error) {
        return false;
    }
};

const interpolate = (template = '', values = {}) => String(template).replace(
    /\{\{(\w+)\}\}/g,
    (_match, key) => values[key] ?? ''
);

/**
 * Reads a SkillPermission answer envelope.
 *
 * The chat answers a permission card with a JSON envelope rather than prose. It
 * names the button that was pressed and the card it belongs to, so the scenario
 * can tell which of several open gates is being answered.
 *
 * @param {string} content - A user message's content.
 * @returns {{action: string, permissionId: string|null, capabilityKey: string|null,
 *            skillId: string|null}|null}
 */
const extractPermissionAnswer = (content) => {
    if (typeof content !== 'string' || !content.includes(SKILL_PERMISSION_RESPONSE_TYPE)) return null;
    try {
        const parsed = JSON.parse(content);
        if (parsed?.type !== SKILL_PERMISSION_RESPONSE_TYPE || !parsed.action) return null;

        return {
            action: parsed.action,
            permissionId: parsed.permissionId || null,
            capabilityKey: parsed.capabilityKey || null,
            skillId: parsed.skillId || null
        };
    } catch {
        return null;
    }
};

const extractFormAnswer = (content) => {
    if (typeof content !== 'string' || !content.includes(FORM_RESPONSE_TYPE)) return null;
    try {
        const parsed = JSON.parse(content);
        return parsed?.type === FORM_RESPONSE_TYPE ? parsed : null;
    } catch {
        return null;
    }
};

/**
 * Reads the workflow a form submission belongs to, so a submitted form can be
 * routed back to its own flow while other flows are open.
 * @param {string} prompt - A lowercased user message.
 * @returns {string|null} - workflowId (or layoutName), or null for plain prompts.
 */
const extractFormKey = (prompt) => {
    if (!prompt || prompt[0] !== '{') return null;
    try {
        const parsed = JSON.parse(prompt);
        // The prompt is lowercased before it reaches here, so the keys are too.
        if (parsed.type !== 'form_response') return null;
        return parsed.workflowid || parsed.layoutname || null;
    } catch {
        return null;
    }
};

/**
 * Splits a HAR recording into an array of conversation turns.
 *
 * Each turn contains:
 *   - userPrompt: the user's message that initiates this turn (lowercased)
 *   - formKey: the workflow this turn answers, when the turn is a form submission
 *   - receiveMessages: the server responses to replay for this turn
 *
 * The recording is segmented at each "send" entry that contains a messages array.
 * "receive" entries between two such sends belong to the earlier turn.
 * Ping sends (type: "ping") are skipped — they are not turn boundaries.
 *
 * Example for create_vol.json (4 sends):
 *   Turn 0: "create a volume..." → [receive responses for turn 0]
 *   Turn 1: "volume name vol_1..."→ [receive responses for turn 1]
 *   Turn 2: "working env ID..."  → [receive responses for turn 2]
 *   Turn 3: "Yes"                → [receive responses for turn 3]
 *
 * @param {Array} recording - The full HAR-recorded message array.
 * @returns {Array<{userPrompt: string, permissionAction: string|null, receiveMessages: Array}>}
 */
const parseConversationTurns = (recording) => {
    const turns = [];
    let currentTurn = null;

    for (const entry of recording) {
        if (isUserMessage(entry)) {
            // Start a new turn — any previous turn is now complete
            if (currentTurn) {
                turns.push(currentTurn);
            }
            const userPrompt = extractLastUserContent(entry);
            currentTurn = {
                userPrompt,
                formKey: extractFormKey(userPrompt),
                receiveMessages: []
            };
        } else if (entry.type === 'receive' && currentTurn) {
            currentTurn.receiveMessages.push(entry);
        }
        // Ping sends and other non-user sends are ignored as turn boundaries
    }

    // Push the final turn
    if (currentTurn) {
        turns.push(currentTurn);
    }

    return turns;
};

// ============================================================================
// Conversation Map
// ============================================================================

/**
 * @typedef {Object} ConversationEntry
 * @property {string} initialPrompt - The first user message (key for lookup)
 * @property {Array<{userPrompt: string, formKey: ?string, receiveMessages: Array}>} turns - Ordered turns
 * @property {Map<string, number>} formKeys - workflowId → index of the turn answering it
 * @property {boolean} isMultiTurn - Whether this conversation has more than one turn
 */

/**
 * Builds a map of initial user prompt → conversation entry for all chat recordings.
 * Key: lowercased first user message for case-insensitive matching.
 * Value: ConversationEntry with all turns for multi-turn replay.
 * @returns {Map<string, ConversationEntry>}
 */
const buildConversationMap = () => {
    const map = new Map();

    allChatRecordings.forEach((recording) => {
        const turns = parseConversationTurns(recording);
        if (turns.length === 0) return;

        const initialPrompt = turns[0].userPrompt;
        if (!initialPrompt) return;

        // A submitted form jumps straight to the turn that answers it, which also
        // skips turns the user never sent (create_vol records its prompt twice).
        const formKeys = new Map();
        turns.forEach((turn, index) => {
            if (turn.formKey && !formKeys.has(turn.formKey)) formKeys.set(turn.formKey, index);
        });

        const entry = {
            initialPrompt,
            turns,
            formKeys,
            isMultiTurn: turns.length > 1
        };

        map.set(initialPrompt, entry);
        console.log(
            `AI Chat: Registered conversation — "${initialPrompt.substring(0, 60)}..." ` +
            `(${turns.length} turn(s), ${turns.reduce((sum, t) => sum + t.receiveMessages.length, 0)} messages)`
        );
    });

    console.log(`AI Chat: ${map.size} conversation(s) loaded`);
    return map;
};

/** @type {Map<string, ConversationEntry>} */
let conversationMap = null;

/** Key used to look up the default fallback conversation (welcome.json) */
const FALLBACK_KEY = extractLastUserContent(chatWelcome.find(e => isUserMessage(e)));

/**
 * Sets the conversation map (used by server lifecycle).
 * @param {Map<string, ConversationEntry>|null} map
 */
const setConversationMap = (map) => {
    conversationMap = map;
};

// ============================================================================
// Message Matching
// ============================================================================

/**
 * Common phrases that should trigger the welcome conversation.
 * These are greetings or help requests that indicate the user wants an introduction.
 */
const WELCOME_PHRASES = [
    'hi',
    'hello',
    'hola',
    'hey',
    'what can you help me with',
    'what can you help me with?',
    'what can you do',
    'what can you do?',
    'help'
];

/**
 * Finds the best matching conversation entry for a given user message.
 * Used to identify which recorded conversation to start.
 * Matching strategy (in order of priority):
 *   0. Welcome phrases — triggers welcome.json for greetings/help requests
 *   1. Exact match (case-insensitive)
 *   2. Recorded prompt contained within user message, or vice versa
 *   3. Word-overlap similarity — picks the recording with the most shared words
 *   4. Falls back to the first registered conversation
 *
 * @param {string} userMessage - The user's incoming message.
 * @returns {ConversationEntry|null} - The matched conversation entry.
 */
const findConversation = (userMessage) => {
    if (!conversationMap || conversationMap.size === 0) {
        console.warn('AI Chat: No conversations loaded');
        return null;
    }

    const normalizedInput = userMessage.toLowerCase().trim();

    // 0. Check for welcome phrases
    if (WELCOME_PHRASES.includes(normalizedInput)) {
        const welcomeConv = FALLBACK_KEY && conversationMap.get(FALLBACK_KEY);
        if (welcomeConv) {
            console.log('AI Chat: Welcome phrase detected, using welcome conversation');
            return welcomeConv;
        }
    }

    // 1. Exact match
    if (conversationMap.has(normalizedInput)) {
        console.log('AI Chat: Exact match found');
        return conversationMap.get(normalizedInput);
    }

    // 2. Substring match
    for (const [prompt, entry] of conversationMap) {
        if (normalizedInput.includes(prompt) || prompt.includes(normalizedInput)) {
            console.log(`AI Chat: Substring match — "${prompt.substring(0, 50)}..."`);
            return entry;
        }
    }

    // 3. Word-overlap similarity
    const inputWords = new Set(normalizedInput.split(/\s+/));
    let bestMatch = null;
    let bestScore = 0;

    for (const [prompt, entry] of conversationMap) {
        const promptWords = prompt.split(/\s+/);
        const score = promptWords.filter(word => inputWords.has(word)).length;
        if (score > bestScore) {
            bestScore = score;
            bestMatch = entry;
        }
    }

    if (bestMatch && bestScore > 0) {
        console.log(`AI Chat: Best word-overlap match (score: ${bestScore})`);
        return bestMatch;
    }

    // 4. Fallback — welcome conversation
    const fallback = (FALLBACK_KEY && conversationMap.get(FALLBACK_KEY)) || conversationMap.values().next().value;
    console.log('AI Chat: No match found, using fallback (welcome) conversation');
    return fallback;
};

// ============================================================================
// Replay Engine
// ============================================================================

/** Track active replay per socket to allow concurrent conversations */
let activeReplayId = 0;

/**
 * Resets the active replay ID counter (used by server lifecycle on shutdown).
 */
const resetActiveReplayId = () => {
    activeReplayId = 0;
    skillPermissionSessionId = 0;
    sessionSeq = 0;
};

/**
 * Computes the delay between consecutive chat messages based on their recorded timestamps.
 * Uses the time difference from the HAR recording, clamped to a max of 2 seconds
 * to keep the offline replay responsive.
 * @param {number} currentTime - The timestamp of the current message (seconds).
 * @param {number} nextTime - The timestamp of the next message (seconds).
 * @returns {number} - The delay in milliseconds.
 */
const getReplayDelay = (currentTime, nextTime) => {
    if (!nextTime || !currentTime) return 50;
    const diffMs = Math.max(0, (nextTime - currentTime) * 1000);
    // Clamp to max 2s to keep the demo snappy, min 20ms for rapid sequences
    return Math.min(Math.max(diffMs, 20), 2000);
};

/**
 * Replays a single turn's receive messages over the WebSocket connection
 * with realistic timing based on the original HAR timestamps.
 * @param {Object} socket - The WebSocket connection object.
 * @param {Array} receiveMessages - The server response messages for this turn.
 * @param {number} replayId - The replay session ID for cancellation support.
 * @returns {Promise<boolean>} - true if completed, false if cancelled.
 */
const replayTurn = async (socket, receiveMessages, replayId) => {
    for (let i = 0; i < receiveMessages.length; i++) {
        // If a newer replay was started, stop this one
        if (replayId !== activeReplayId) {
            console.log('AI Chat: Replay cancelled (new conversation started)');
            return false;
        }

        const message = receiveMessages[i];
        const nextMessage = receiveMessages[i + 1];

        console.log('AI Chat: Sending message:', message.data?.substring?.(0, 80) || message.data);
        socket.send(message.data);

        const delay = getReplayDelay(message.time, nextMessage?.time);
        await sleep(delay);
    }

    return true;
};

// ============================================================================
// Dynamic SkillPermission Scenario
// ============================================================================

let skillPermissionSessionId = 0;

/**
 * The simulated task: look at the cluster, then create a volume on it. Each step
 * names the skill and the capability that gates it, so the single prompt covers
 * every combination of the two capability levels the user picks in the panel.
 *
 * Read-only mode drops the agent-only step. The chat forces Modify storage off
 * whenever agent mode is off, so a Modify storage card there would report the
 * mode rather than anything the user chose.
 */
const SCENARIO_STEPS = [
    {
        capabilityKey: 'analyzer',
        capabilityName: 'Analyze storage',
        skillId: 'analyze_capacity',
        skillName: 'Analyze capacity',
        result: 'Cluster prod-01 is at 68% capacity, with 4.2 TiB free.'
    },
    {
        capabilityKey: 'analyzer',
        capabilityName: 'Analyze storage',
        skillId: 'check_cluster_health',
        skillName: 'Check cluster health',
        result: 'All four nodes on prod-01 report healthy.'
    },
    {
        capabilityKey: 'storage_management',
        capabilityName: 'Modify storage',
        skillId: 'create_volume',
        skillName: 'Create volume',
        agentOnly: true,
        result: 'Created demo volume vol_demo_01 (100 GiB) on cluster prod-01.'
    }
];

const CARD_GAP_SECONDS = 0.25;

// A running card is replaced by its completed one under the same permissionId,
// so without a dwell the loader would be swapped out before it is ever seen.
const LOADER_DWELL_SECONDS = 1.2;
const IN_PROGRESS_CARD_STATUSES = new Set(['using', 'approved_using']);

const createReceiveEntry = (event, offsetSeconds) => ({
    type: 'receive',
    time: (Date.now() / 1000) + offsetSeconds,
    opcode: 1,
    data: JSON.stringify({ ...event, timestamp: Date.now() + Math.round(offsetSeconds * 1000) })
});

const createRunEvents = (session, cards, text, extraEvents = []) => {
    const runId = `${session.threadId}-run-${++session.runNumber}`;
    const events = [
        createReceiveEntry({
            type: 'RUN_STARTED',
            data: { threadId: session.threadId, runId }
        }, 0)
    ];

    let offset = 0.05;
    cards.forEach((data) => {
        events.push(createReceiveEntry({
            type: 'CUSTOM',
            data: {
                name: 'UIDescriptor',
                value: {
                    component: 'SkillPermission',
                    data: { re_seq: 'INSTANT', ...data }
                }
            }
        }, offset));
        offset += IN_PROGRESS_CARD_STATUSES.has(data.status)
            ? LOADER_DWELL_SECONDS
            : CARD_GAP_SECONDS;
    });

    if (text) {
        const messageId = `${runId}-message`;
        events.push(
            createReceiveEntry({
                type: 'TEXT_MESSAGE_START',
                data: { messageId, role: 'assistant' }
            }, offset),
            createReceiveEntry({
                type: 'TEXT_MESSAGE_CONTENT',
                data: { messageId, delta: text }
            }, offset + 0.01),
            createReceiveEntry({
                type: 'TEXT_MESSAGE_END',
                data: { messageId }
            }, offset + 0.02)
        );
        offset += 0.05;
    }

    extraEvents.forEach((event) => {
        events.push(createReceiveEntry(event, offset));
        offset += CARD_GAP_SECONDS;
    });

    events.push(createReceiveEntry({
        type: 'RUN_FINISHED',
        data: {
            threadId: session.threadId,
            runId,
            result: {
                agent: session.agent || 'Coordinator',
                intent: session.intent || 'sm_create_volume'
            }
        }
    }, offset));

    return events;
};

// A capability gate is raised once per capability; a skill gate once per skill.
// Separate ids give every skill its own card, while a card's own progression
// (running, then done) keeps updating in place.
const capabilityPermissionId = (session, capabilityKey) =>
    `perm_sim_${session.id}_cap_${capabilityKey}`;

const skillPermissionId = (session, skillId) =>
    `perm_sim_${session.id}_skill_${skillId}`;

const capabilityCard = (session, step, status) => ({
    permissionId: capabilityPermissionId(session, step.capabilityKey),
    status,
    capabilityKey: step.capabilityKey,
    capabilityName: step.capabilityName,
    ...(status === 'capability_off' && session.workflow?.permissionActions?.capabilityOff && {
        actions: session.workflow.permissionActions.capabilityOff.map((id) => ({ id }))
    }),
    ...(status === 'capability_off' && {
        description: 'Your task requires this capability. Enable it to continue.'
    })
});

const skillCard = (session, step, status) => ({
    permissionId: skillPermissionId(session, step.skillId),
    status,
    skillId: step.skillId,
    skillName: step.skillName,
    ...(status === 'approval_required' && {
        capabilityKey: step.capabilityKey,
        capabilityName: step.capabilityName,
        ...(session.workflow?.permissionActions?.approvalRequired && {
            actions: session.workflow.permissionActions.approvalRequired.map((id) => ({ id }))
        })
    })
});

const replaySkillPermissionRun = async (socket, session, cards, text, extraEvents = []) => {
    const replayId = ++activeReplayId;
    session.replayId = replayId;
    return replayTurn(socket, createRunEvents(session, cards, text, extraEvents), replayId);
};

const finishSkillPermissionSession = (socket, session) => {
    const sessions = socketSessions.get(socket);
    if (sessions instanceof Map && session?.sessionKey) {
        sessions.delete(session.sessionKey);
        if (sessions.size === 0) socketSessions.delete(socket);
        return;
    }
    socketSessions.delete(socket);
};

const deleteVolumeLayoutId = (session) => `delete-volume-layout-${session.id}`;

/**
 * The form arrives the way a real backend sends one: a layout that owns the
 * workflow, with the fields in a grid cell and the accept/cancel bar pinned to
 * the bottom of the canvas.
 */
const createDeleteVolumeLayoutEvent = (session) => ({
    type: 'CUSTOM',
    data: {
        name: 'LayoutGrid',
        value: {
            id: session.workflow.layout.id,
            name: session.workflow.layout.name,
            layoutId: deleteVolumeLayoutId(session),
            workflowId: session.workflowId,
            skillName: session.workflow.skillName,
            title: session.workflow.layout.title,
            layout: [
                { i: session.workflow.layout.formCellId, component: 'DynamicForm' },
                {
                    i: session.workflow.layout.actionsCellId,
                    component: 'ConfirmationBar',
                    ds: 'ui',
                    pin: 'bottom',
                    props: {
                        confirmLabel: session.workflow.layout.confirmLabel,
                        cancelLabel: session.workflow.layout.cancelLabel,
                        destructive: session.workflow.layout.destructive,
                        destructiveField: session.workflow.layout.destructiveField
                    }
                }
            ]
        }
    }
});

/**
 * Each dropdown is populated from the one above it, so the cluster decides which
 * storage VMs exist and the storage VM decides which volumes can be deleted.
 */
const createDeleteVolumeFormEvent = (session) => ({
    type: 'CUSTOM',
    data: {
        name: 'UIDescriptor',
        value: {
            layoutId: deleteVolumeLayoutId(session),
            cellId: session.workflow.layout.formCellId,
            component: 'DynamicForm',
            data: {
                id: session.workflow.layout.formCellId,
                heading: session.workflow.layout.form.heading,
                text: session.workflow.layout.form.text,
                fields: session.workflow.layout.form.fields.map((field) => {
                    if (field.optionsSource !== 'simulatorClusters') return field;
                    const { optionsSource, ...rest } = field;
                    return {
                        ...rest,
                        options: listSimulatorClusters().map(({ id, name }) => ({
                            label: name,
                            value: id
                        }))
                    };
                })
            }
        }
    }
});

const openDeleteVolumeForm = async (socket, session, cards) => {
    session.pending = null;
    session.stage = 'awaiting_form';
    return replaySkillPermissionRun(
        socket,
        session,
        cards,
        [
            ...session.resultLines,
            session.workflow.layout.openedText
        ].join(' '),
        [createDeleteVolumeLayoutEvent(session), createDeleteVolumeFormEvent(session)]
    );
};

/**
 * Walks the remaining steps, collecting cards until one needs an answer.
 *
 * Steps whose capability is on "allow" run without asking, so a stretch of them
 * is replayed as one run. The walk stops at the first step gated behind "off"
 * (the capability gate) or "ask" (a per-skill approval), and the answer resumes
 * it from the same place. The answer's own cards are passed in as a prefix so
 * they share the run with whatever comes next.
 *
 * @param {Object} socket
 * @param {Object} session
 * @param {{cards?: Array, lines?: Array}} [prefix] - Cards and text from the
 *        answer that resumed the walk.
 */
const advanceSkillPermissionScenario = async (socket, session, prefix = {}) => {
    const cards = [...(prefix.cards || [])];
    session.resultLines.push(...(prefix.lines || []));

    while (session.stepIndex < session.steps.length) {
        const step = session.steps[session.stepIndex];
        const level = session.levels[step.capabilityKey];

        if (level === 'off') {
            session.pending = { type: 'capability', capabilityKey: step.capabilityKey };
            cards.push(capabilityCard(session, step, 'capability_off'));
            return replaySkillPermissionRun(socket, session, cards, '');
        }

        if (level === 'ask') {
            session.pending = { type: 'skill', skillId: step.skillId };
            cards.push(skillCard(session, step, 'approval_required'));
            return replaySkillPermissionRun(socket, session, cards, '');
        }

        cards.push(skillCard(session, step, 'using'), skillCard(session, step, 'used'));
        session.resultLines.push(step.result);
        session.stepIndex += 1;
    }

    session.pending = null;

    if (session.kind === 'simulator-workflow') {
        return openDeleteVolumeForm(socket, session, cards);
    }

    // The last answer may have completed an empty scenario, leaving nothing to
    // render or say.
    if (cards.length === 0 && session.resultLines.length === 0) {
        finishSkillPermissionSession(socket, session);
        return true;
    }

    const completed = await replaySkillPermissionRun(
        socket,
        session,
        cards,
        session.resultLines.join(' ')
    );
    finishSkillPermissionSession(socket, session);
    return completed;
};

const startSkillPermissionScenario = async (socket, request) => {
    const id = ++skillPermissionSessionId;
    const context = parseRequestContext(request);
    const mode = context[MODE_CONTEXT] || 'act';
    const isAgentMode = mode !== 'discuss';
    const levels = {
        analyzer: getCapabilityLevel(context, 'analyzer'),
        storage_management: getCapabilityLevel(context, 'storage_management')
    };
    const steps = SCENARIO_STEPS.filter(step => isAgentMode || !step.agentOnly);

    // Which cards appear is decided entirely by these values, so log them: mode
    // forces Modify storage off no matter what the panel shows.
    console.log(
        `AI Chat: SkillPermission scenario — mode "${mode}", Analyze storage `
        + `"${levels.analyzer}", Modify storage "${levels.storage_management}", `
        + `${steps.length} step(s)`
    );

    const session = {
        kind: SKILL_PERMISSION_SESSION,
        id,
        levels,
        steps,
        stepIndex: 0,
        pending: null,
        resultLines: [],
        threadId: `skill-permission-thread-${id}`,
        runNumber: 0,
        replayId: null
    };
    const sessions = sessionsFor(socket);
    session.sessionKey = SKILL_PERMISSION_SESSION;
    sessions.set(session.sessionKey, session);

    return advanceSkillPermissionScenario(socket, session);
};

const startSimulatorWorkflow = async (socket, request, workflow) => {
    const id = ++skillPermissionSessionId;
    const context = parseRequestContext(request);
    const mode = context[MODE_CONTEXT] || 'discuss';
    const steps = workflow.steps.map((step) => ({
        ...step,
        capabilityName: workflow.capabilities[step.capabilityKey]?.name || step.capabilityKey
    }));
    const session = {
        kind: 'simulator-workflow',
        workflow,
        sessionKey: `simulator-workflow:${workflow.id}`,
        id,
        levels: Object.fromEntries(Object.keys(workflow.capabilities).map(
            (key) => [key, getCapabilityLevel(context, key, workflow)]
        )),
        steps,
        stepIndex: 0,
        pending: null,
        resultLines: [],
        stage: 'capabilities',
        workflowId: `workflow-delete-volume-${id}`,
        threadId: `delete-volume-thread-${id}`,
        runNumber: 0,
        replayId: null,
        intent: workflow.intent,
        agent: workflow.agent
    };

    if (mode !== workflow.mode.required) {
        const completed = await replaySkillPermissionRun(socket, session, [], workflow.mode.blockedText);
        if (sessionsFor(socket).size === 0) socketSessions.delete(socket);
        return completed;
    }

    console.log(
        `AI Chat: ${workflow.id} — `
        + Object.entries(session.levels).map(([key, level]) => `${key} "${level}"`).join(', ')
    );
    sessionsFor(socket).set(session.sessionKey, session);
    return advanceSkillPermissionScenario(socket, session);
};

/** The card the scenario is currently waiting on. */
const pendingPermissionId = (session, step) => (session.pending?.type === 'capability'
    ? capabilityPermissionId(session, step.capabilityKey)
    : skillPermissionId(session, step.skillId));

/**
 * Applies an answer to the gate the scenario is waiting on.
 *
 * Approving or enabling resumes the walk through the remaining steps; denying or
 * cancelling ends the task there, so the steps behind the refused one never run.
 */
const handleSkillPermissionAnswer = async (socket, session, answer) => {
    const step = session.steps[session.stepIndex];

    if (!step || !session.pending) return false;

    if (answer.permissionId && answer.permissionId !== pendingPermissionId(session, step)) {
        console.log(`AI Chat: SkillPermission answer for "${answer.permissionId}" is not the open card — ignoring`);
        return false;
    }

    const configuredActions = session.pending.type === 'capability'
        ? session.workflow?.permissionActions?.capabilityOff
        : session.workflow?.permissionActions?.approvalRequired;
    if (configuredActions && !configuredActions.includes(answer.action)) return false;

    session.pending = null;

    if (answer.action === 'cancel') {
        const completion = session.workflow
            ? interpolate(session.workflow.outcomes.capabilityCancelled, step)
            : `${step.capabilityName} stays turned off, so I stopped before running `
                + `${step.skillName}. Nothing has changed.`;
        await replaySkillPermissionRun(
            socket,
            session,
            [capabilityCard(session, step, 'capability_cancelled')],
            [...session.resultLines, completion].join(' ')
        );
        finishSkillPermissionSession(socket, session);
        return true;
    }

    if (answer.action === 'deny') {
        const completion = session.workflow
            ? interpolate(session.workflow.outcomes.skillDenied, step)
            : `Understood — I skipped ${step.skillName} and stopped there. Nothing has changed.`;
        await replaySkillPermissionRun(
            socket,
            session,
            [skillCard(session, step, 'denied')],
            [...session.resultLines, completion].join(' ')
        );
        finishSkillPermissionSession(socket, session);
        return true;
    }

    if (answer.action === 'enable_ask' || answer.action === 'enable_always') {
        session.levels[step.capabilityKey] = answer.action === 'enable_ask' ? 'ask' : 'allow';

        return advanceSkillPermissionScenario(socket, session, {
            cards: [capabilityCard(session, step, 'capability_enabled')],
            lines: [
                session.workflow
                    ? interpolate(session.workflow.outcomes.capabilityEnabled, step)
                    : `${step.capabilityName} is enabled for this session.`
            ]
        });
    }

    if (answer.action === 'approve') {
        session.stepIndex += 1;

        return advanceSkillPermissionScenario(socket, session, {
            cards: [
                skillCard(session, step, 'approved_using'),
                skillCard(session, step, 'approved_used')
            ],
            lines: [step.result]
        });
    }

    return false;
};

const handleDeleteVolumeFormAnswer = async (socket, session, answer) => {
    if (session.stage !== 'awaiting_form') return false;
    if (answer.workflowId && answer.workflowId !== session.workflowId) {
        console.log(`AI Chat: Form answer for "${answer.workflowId}" is not the open delete workflow — ignoring`);
        return false;
    }
    if (!['accept', 'cancel'].includes(answer.action)) return false;

    session.stage = 'completed';
    const text = answer.action === 'accept'
        ? interpolate(session.workflow.outcomes.success, answer.payload)
        : session.workflow.outcomes.formCancelled;

    await replaySkillPermissionRun(socket, session, [], text);
    finishSkillPermissionSession(socket, session);
    return true;
};

// ============================================================================
// Stop Scenarios
// ============================================================================

const STOP_PATH = 'v1/management/coordinator/chat/stop';

// The outcome arrives a moment after the stop request, so "Stopping" is visible.
const STOP_RESPONSE_SECONDS = 1;

// A workflow repeats a skill across volumes, so the index keeps each operation's card separate.
const operationPermissionId = (session, operation, index) =>
    `${skillPermissionId(session, operation.skillId)}_${index}`;

const operationCard = (session, operation, index, status) => ({
    ...skillCard(session, operation, status),
    permissionId: operationPermissionId(session, operation, index)
});

/**
 * Starts a run the user can stop. A workflow replays its operations and finishes
 * on its own if nobody stops it. A fixed scenario only opens the run, so nothing
 * on screen contradicts its preset outcome, and it stays open until Stop.
 */
const startStopScenario = async (socket, scenario) => {
    const id = ++skillPermissionSessionId;
    const session = {
        kind: STOP_SCENARIO_SESSION,
        sessionKey: STOP_SCENARIO_SESSION,
        id,
        scenario,
        entries: [],
        threadId: `stop-thread-${id}`,
        runNumber: 0,
        replayId: null,
        intent: stopScenarios.intent,
        agent: stopScenarios.agent
    };
    sessionsFor(socket).set(session.sessionKey, session);
    console.log(`AI Chat: Stop scenario "${scenario.id}" — select Stop while it runs`);

    if (!scenario.operations) {
        session.entries = [createReceiveEntry({
            type: 'RUN_STARTED',
            data: { threadId: session.threadId, runId: `${session.threadId}-run-${++session.runNumber}` }
        }, 0)];
    } else {
        const cards = scenario.operations.flatMap((operation, index) => [
            operationCard(session, operation, index, 'using'),
            operationCard(session, operation, index, 'used')
        ]);
        session.entries = createRunEvents(session, cards, scenario.summary);
    }

    const replayId = ++activeReplayId;
    session.replayId = replayId;
    const completed = await replayTurn(socket, session.entries, replayId);
    if (completed && scenario.operations) finishSkillPermissionSession(socket, session);
    return completed;
};

/** Each operation card's latest status among the run's entries that were due by now. */
const sentCardStatuses = (session) => {
    const now = Date.now() / 1000;
    return session.entries
        .filter((entry) => entry.time <= now)
        .map((entry) => JSON.parse(entry.data))
        .filter((event) => event.data?.value?.component === 'SkillPermission')
        .reduce((statuses, event) => statuses.set(
            event.data.value.data.permissionId,
            event.data.value.data.status
        ), new Map());
};

/** What the run had done when Stop arrived: a fixed scenario's preset, or the workflow's progress. */
const resolveStopOutcome = (session) => {
    const { scenario } = session;
    if (!scenario.operations) return { outcome: scenario.outcome, rows: scenario.rows || [] };

    const statuses = sentCardStatuses(session);
    if (statuses.size === 0) return { outcome: 'noActions', rows: [] };

    const rows = scenario.operations.map((operation, index) => {
        const status = statuses.get(operationPermissionId(session, operation, index));
        if (status === 'used') return [operation.doneLabel, 'succeeded'];
        if (status === 'using') return [operation.label, 'partially_completed'];
        return [operation.label, 'not_started'];
    });
    const allCompleted = rows.every(([, status]) => status === 'succeeded');
    return { outcome: allCompleted ? 'allCompleted' : 'partial', rows };
};

/**
 * What a backend sends after Stop, within the stopped run: the outcome sentence,
 * a ChangesTable when something changed, then RUN_FINISHED. A scenario without
 * an outcome sends only RUN_FINISHED, so the chat shows its fallback.
 */
const createStopOutcomeEvents = (session) => {
    const { outcome, rows } = resolveStopOutcome(session);
    const runId = `${session.threadId}-run-${session.runNumber}`;
    const messageId = `${runId}-stopped`;
    const events = [];

    if (outcome) {
        events.push(
            { type: 'TEXT_MESSAGE_START', data: { messageId, role: 'assistant' } },
            { type: 'TEXT_MESSAGE_CONTENT', data: { messageId, delta: stopScenarios.outcomes[outcome] } },
            { type: 'TEXT_MESSAGE_END', data: { messageId } }
        );
    }

    if (rows.length > 0) {
        events.push({
            type: 'CUSTOM',
            data: {
                name: 'UIDescriptor',
                value: {
                    component: 'ChangesTable',
                    descriptorId: `changes-${session.threadId}`,
                    data: { ...stopScenarios.table, rows }
                }
            }
        });
    }

    events.push({
        type: 'RUN_FINISHED',
        data: {
            threadId: session.threadId,
            runId,
            result: { agent: session.agent, intent: session.intent }
        }
    });

    return events.map((event, index) => createReceiveEntry(event, index * 0.01));
};

/** The stop scenario running on the conversation the chat asked to stop. */
const findStopSession = (conversationId) => {
    for (const [socket, sessions] of socketSessions) {
        const session = sessions.get(STOP_SCENARIO_SESSION);
        if (session?.threadId === conversationId) return { socket, session };
    }
    return null;
};

const stopSimulatedRun = async (socket, session) => {
    const events = createStopOutcomeEvents(session);
    // A newer replay id cancels the running replay, as a new conversation does.
    const replayId = ++activeReplayId;
    session.replayId = replayId;
    finishSkillPermissionSession(socket, session);
    console.log(`AI Chat: Stop requested for ${session.threadId}`);

    await sleep(STOP_RESPONSE_SECONDS * 1000);
    return replayTurn(socket, events, replayId);
};

/**
 * Registers the chat's stop endpoint on the Mirage server.
 * Only stop scenarios can be stopped; any other run answers 404.
 * @param {Object} route - The Mirage route object being configured.
 */
const seedAiStopRoute = (route) => {
    route.post(`/${STOP_PATH}`, (_schema, req) => {
        const body = JSON.parse(req.requestBody);
        const match = findStopSession(body.conversation_id);
        if (!match) return new Response(404, {}, { message: 'No simulated run to stop' });

        stopSimulatedRun(match.socket, match.session);
        return {};
    });
};

// ============================================================================
// Active Session Tracking
// ============================================================================

/**
 * @typedef {Object} Session
 * @property {ConversationEntry} conversation
 * @property {number} currentTurnIndex - Turn last replayed for this conversation
 * @property {number} replayId
 * @property {number} lastActiveSeq - Ordering used to pick the most recent flow
 */

/**
 * Open conversations per socket, keyed by the conversation's initial prompt.
 * A socket holds several at once so a new request (say "create igroup") can run
 * while an earlier flow is still waiting for its form to be submitted.
 * @type {Map<Object, Map<string, Session>>}
 */
const socketSessions = new Map();

/** Monotonic counter marking which session was used most recently. */
let sessionSeq = 0;

const sessionsFor = (socket) => {
    let sessions = socketSessions.get(socket);
    if (!sessions) {
        sessions = new Map();
        socketSessions.set(socket, sessions);
    }
    return sessions;
};

/** The open session whose conversation owns this workflow, if any. */
const findSessionByFormKey = (sessions, formKey) => {
    for (const [key, session] of sessions) {
        if (session.conversation?.formKeys?.has(formKey)) return { key, session };
    }
    return null;
};

/** The recorded conversation that answers this workflow, if any. */
const findConversationByFormKey = (formKey) => {
    if (!conversationMap) return null;
    for (const conversation of conversationMap.values()) {
        if (conversation.formKeys.has(formKey)) return conversation;
    }
    return null;
};

/**
 * Replays the recorded turn that answers a submitted form, reopening its
 * conversation when the flow was already replayed. An answer no recording
 * claims is stale (a card from an earlier run), so it is dropped rather than
 * read as a new prompt.
 */
const routeRecordedFormAnswer = async (socket, sessions, formKey) => {
    const open = formKey && findSessionByFormKey(sessions, formKey);
    if (open) {
        return runTurn(socket, sessions, open.key, open.session, open.session.conversation.formKeys.get(formKey));
    }

    const conversation = formKey && findConversationByFormKey(formKey);
    if (conversation) {
        const session = startSession(sessions, conversation);
        return runTurn(socket, sessions, conversation.initialPrompt, session, conversation.formKeys.get(formKey));
    }

    console.warn(`AI Chat: No recorded turn answers workflow "${formKey}"`);
    return false;
};

/**
 * An open session whose next turn was recorded against exactly this prompt.
 * Recordings repeat a prompt across turns (create_vol answers it once in
 * read-only mode and again in read/write), so this resumes instead of restarting.
 */
const findSessionAwaitingPrompt = (sessions, prompt) => {
    for (const [key, session] of sessions) {
        if (session.conversation?.turns?.[session.currentTurnIndex + 1]?.userPrompt === prompt) {
            return { key, session };
        }
    }
    return null;
};

/** The flow the user interacted with last, used when nothing else matches. */
const mostRecentSession = (sessions) => {
    let recent = null;
    for (const [key, session] of sessions) {
        if (!session.conversation) continue;
        if (!recent || session.lastActiveSeq > recent.session.lastActiveSeq) recent = { key, session };
    }
    return recent;
};

/**
 * Replays one turn of a session and drops the session once it runs out of turns.
 * @param {Object} socket
 * @param {Map<string, Session>} sessions
 * @param {string} key - Session key (the conversation's initial prompt).
 * @param {Session} session
 * @param {number} turnIndex
 */
const runTurn = async (socket, sessions, key, session, turnIndex) => {
    const { conversation } = session;
    if (turnIndex < 0 || turnIndex >= conversation.turns.length) {
        console.log(`AI Chat: "${key.substring(0, 40)}" has no turn ${turnIndex + 1} — ignoring`);
        sessions.delete(key);
        return;
    }

    session.currentTurnIndex = turnIndex;
    session.lastActiveSeq = ++sessionSeq;
    const replayId = ++activeReplayId;
    session.replayId = replayId;

    console.log(
        `AI Chat: "${key.substring(0, 40)}" — replaying turn ${turnIndex + 1}/${conversation.turns.length}`
    );
    const completed = await replayTurn(socket, conversation.turns[turnIndex].receiveMessages, replayId);

    if (completed && turnIndex >= conversation.turns.length - 1) {
        console.log(`AI Chat: "${key.substring(0, 40)}" fully replayed`);
        sessions.delete(key);
    } else if (completed) {
        console.log(`AI Chat: Turn complete — "${key.substring(0, 40)}" waiting for user input...`);
    }
};

/** Opens (or restarts) a conversation as its own session. */
const startSession = (sessions, conversation) => {
    const session = {
        conversation,
        currentTurnIndex: -1,
        replayId: 0,
        lastActiveSeq: ++sessionSeq
    };
    sessions.set(conversation.initialPrompt, session);
    return session;
};

/**
 * Handles an incoming user message on a socket, routing it to the flow it
 * belongs to. Flows may interleave, so the message itself decides the target:
 *   1. A submitted form names its workflow — it always answers that flow.
 *   2. A prompt an open flow is waiting for continues that flow.
 *   3. A prompt that exactly matches a recording's opener starts that flow,
 *      alongside any flow already open.
 *   4. Anything else continues the most recently used flow, or starts the
 *      closest matching recording when nothing is open.
 *
 * @param {Object} socket - The WebSocket connection object.
 * @param {string} userContent - The user's message content.
 * @param {Object|null} request - Full chat request, including capability context.
 */
const handleUserMessage = async (socket, userContent, request = null) => {
    const sessions = sessionsFor(socket);
    const prompt = String(userContent || '').toLowerCase().trim();

    // Simulator answers carry stable ids, so route them before ordinary HAR turns.
    const permissionAnswer = extractPermissionAnswer(userContent);
    if (permissionAnswer) {
        const match = [...sessions.values()].find((session) => (
            session.pending
            && (!permissionAnswer.permissionId
                || pendingPermissionId(session, session.steps[session.stepIndex]) === permissionAnswer.permissionId)
        ));
        return match ? handleSkillPermissionAnswer(socket, match, permissionAnswer) : false;
    }

    const formAnswer = extractFormAnswer(userContent);
    if (formAnswer) {
        const match = [...sessions.values()].find((session) => (
            session.kind === 'simulator-workflow'
            && session.workflowId === formAnswer.workflowId
        ));
        if (match) return handleDeleteVolumeFormAnswer(socket, match, formAnswer);
        return routeRecordedFormAnswer(socket, sessions, extractFormKey(prompt));
    }

    const workflow = SIMULATOR_WORKFLOWS.find((candidate) => matchesSimulatorWorkflow(candidate, prompt));
    if (workflow) return startSimulatorWorkflow(socket, request, workflow);
    const stopScenario = STOP_SCENARIOS.find((candidate) => matchesSimulatorWorkflow(candidate, prompt));
    if (stopScenario) return startStopScenario(socket, stopScenario);
    if (SKILL_PERMISSION_PROMPT_PATTERN.test(prompt)) {
        return startSkillPermissionScenario(socket, request);
    }

    // 2. Continue a flow that is waiting for this exact prompt.
    const waiting = findSessionAwaitingPrompt(sessions, prompt);
    if (waiting) {
        return runTurn(socket, sessions, waiting.key, waiting.session, waiting.session.currentTurnIndex + 1);
    }

    // 3. A recorded opener starts its own flow, even while others are open.
    const opener = conversationMap?.get(prompt);
    if (opener) {
        const session = startSession(sessions, opener);
        return runTurn(socket, sessions, opener.initialPrompt, session, 0);
    }

    // 4. Fall back to the most recent flow, then to a fuzzy match.
    const recent = mostRecentSession(sessions);
    if (recent) {
        return runTurn(socket, sessions, recent.key, recent.session, recent.session.currentTurnIndex + 1);
    }

    const conversation = findConversation(prompt);
    if (!conversation) {
        socket.send(JSON.stringify({
            type: 'RUN_ERROR',
            timestamp: Date.now(),
            data: { message: 'No recorded conversation matches this prompt', code: 'NO_MATCH' }
        }));
        return;
    }

    const session = startSession(sessions, conversation);
    return runTurn(socket, sessions, conversation.initialPrompt, session, 0);
};

export {
    buildConversationMap,
    setConversationMap,
    resetActiveReplayId,
    socketSessions,
    handleUserMessage,
    seedAiStopRoute
};
